import { gql, useLazyQuery } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import DownloadApp from "@/components/download-app";
import Header from "@/components/header-v2";
import { Button } from "@/components/ui/button";
import { API_CMS_PROVIDER } from "@/helpers/apollo";

interface ImageType {
  attributes: {
    url: string;
  };
}

interface BlogType {
  attributes: {
    image: {
      data: ImageType;
    };
    slug: string;
    title: string;
  };
  id: string;
}

interface PaginationType {
  page: number;
  pageCount: number;
  pageSize?: number;
  total?: number;
}

export interface BlogDataType {
  blogs: {
    data: BlogType[];
    meta: {
      pagination: PaginationType;
    };
  };
}

export interface BlogVariablesType {
  pagination: {
    limit?: number;
    page?: number;
    pageSize: number;
    start?: number;
  };
}

export interface BlogProps {
  blogs: BlogType[];
  pagination: PaginationType;
}

export const GET_BLOGS = gql`
  query Blogs($pagination: PaginationArg!) {
    blogs(pagination: $pagination, sort: "publishedAt:desc") {
      data {
        id
        attributes {
          image {
            data {
              attributes {
                url
              }
            }
          }
          slug
          title
        }
      }
      meta {
        pagination {
          page
          pageCount
        }
      }
    }
  }
`;

export const PAGE_SIZE = 6;

interface BlogPostProps {
  post: BlogType;
}

function BlogPost({ post: { attributes } }: BlogPostProps) {
  return (
    <div className="group relative overflow-hidden rounded-[20px]">
      <img
        alt={attributes.title}
        className="aspect-[305/243] w-full object-cover"
        src={attributes.image.data.attributes.url}
      />
      <h2 className="absolute inset-0 bg-black/40 text-center font-semibold text-20/[2em] opacity-0 transition-opacity group-hover:opacity-100">
        <Link
          className="inset-0 flex translate-y-2.5 items-center justify-center px-[25px] transition-transform group-hover:translate-y-0"
          to={`/blog/${attributes.slug}`}
        >
          {attributes.title}
        </Link>
      </h2>
    </div>
  );
}

export default function Blog({ blogs, pagination }: BlogProps) {
  const [posts, setPosts] = useState(blogs);

  const [fetchMore, { data, loading }] = useLazyQuery<
    BlogDataType,
    BlogVariablesType
  >(GET_BLOGS, {
    context: { clientName: API_CMS_PROVIDER },
  });

  const handleMoreButton = () => {
    const page = data ? data.blogs.meta.pagination.page : pagination.page;

    fetchMore({
      variables: {
        pagination: {
          page: page + 1,
          pageSize: PAGE_SIZE,
        },
      },
    });
  };

  useEffect(() => {
    if (data) {
      setPosts((oldPosts) => [...oldPosts, ...data.blogs.data]);
    }
  }, [data]);

  const hasMorePages = useMemo(() => {
    let p = pagination;

    if (data) {
      p = data.blogs.meta.pagination;
    }

    return p.pageCount > p.page;
  }, [pagination, data]);

  return (
    <>
      <Header variant="simplified" />
      <div className="container">
        <h1 className="mb-9 pt-22 text-24/[1.25em] text-white md:mb-12.5 md:pt-5 md:text-48/[1.5em]">
          Morty blog
        </h1>
        <div className="grid grid-cols-1 gap-9 md:grid-cols-2 md:gap-24 md:px-27.5">
          {posts.map((post) => (
            <BlogPost key={post.id} post={post} />
          ))}
        </div>
        {hasMorePages && (
          <div className="flex justify-center pt-9 md:pt-24">
            <Button
              className="h-14 w-38 rounded-[100px]"
              disabled={loading}
              onClick={handleMoreButton}
              variant="cta"
            >
              Load more
            </Button>
          </div>
        )}
        <DownloadApp className="pt-[247px] pb-20 md:pt-69 md:pb-70" />
      </div>
    </>
  );
}
